// 058 — Cuidados Paliativos (Fase 5, Tanda 6). Los bloques A-R oficiales están
// en `bloques058Datos.ts` (fuente: PDF páginas 209-210). El bloque A lo
// resuelve `EncabezadoMspCampos`, igual que 113/114/115/016/052.
//
// FHIR (solo con consumidor real): `Observation` de signos vitales para J
// (temperatura, presión arterial como panel, pulso, frecuencia respiratoria,
// peso, talla, saturación — alimentan la pestaña Signos Vitales) y un
// `Condition` por diagnóstico con texto (D y O). El IMC se calcula y queda solo
// en el `Par[]`. Todo lo demás, `Par[]` de respaldo en el PDF firmado.
import type { Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF, type CampoSimple, type Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { PA_DIASTOLICA_LOINC, PA_PANEL_LOINC, PA_SISTOLICA_LOINC, signoVitalPorClave, VITAL_SIGNS_CATEGORY } from './vitalSigns';
import {
  CAMPOS_ANTECEDENTES_058,
  CAMPOS_COMPONENTES_058,
  CAMPOS_CONOCIMIENTO_058,
  CAMPOS_CONSTANTES_058,
  CAMPOS_CUIDADOR_058,
  CAMPOS_EXAMEN_FISICO_058,
  CAMPOS_FAMILIOGRAMA_058,
  CAMPOS_HABITOS_058,
  CAMPOS_INFORMANTE_058,
  CAMPOS_MOTIVO_ENFERMEDAD_058,
  CAMPOS_PATOLOGIA_INGRESO_058,
  CAMPOS_PERSONALES_058,
  CAMPOS_PLAN_058,
  CAMPOS_SINTOMAS_058,
  estadoInicialBitacora058,
  imc058,
  paresDeBitacora058,
  sintomasFueraDeRango058,
  type EstadoBitacora058,
} from './bloques058Datos';

const CODIGO = '058';

type ClaveValores = { [K in keyof EstadoBitacora058]: EstadoBitacora058[K] extends Valores ? K : never }[keyof EstadoBitacora058];

function numeroOIndefinido(texto: string | undefined): number | undefined {
  const valor = texto === undefined || texto.trim() === '' ? undefined : Number(texto);
  return valor !== undefined && !Number.isNaN(valor) ? valor : undefined;
}

export function Formulario058({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.058');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Cuidados paliativos: el papel no trae servicio ni fija ámbito.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { sinServicio: true });

  const [estado, setEstado] = useState<EstadoBitacora058>(() => estadoInicialBitacora058());
  const imc = imc058(estado.constantes);

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const fuera = sintomasFueraDeRango058(estado.sintomas);
      if (fuera.length > 0) throw new Error(t('formularios.f058.sintomasFueraDeRango', { campos: fuera.join(', ') }));
      const lote = new LoteTransaccional();
      const pares: Par[] = paresDeBitacora058(t, estado);
      const ahora = new Date().toISOString();
      const c = estado.constantes;
      const observaciones: Observation[] = [];

      const simples: [string, string][] = [
        ['temperatura', 'temperatura'],
        ['fc', 'pulso'],
        ['fr', 'frecuenciaRespiratoria'],
        ['sato2', 'pulsioximetria'],
        ['peso', 'peso'],
        ['talla', 'talla'],
      ];
      for (const [claveSigno, claveCampo] of simples) {
        const valor = numeroOIndefinido(c[claveCampo]);
        if (valor === undefined) continue;
        const signo = signoVitalPorClave(claveSigno);
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: valor, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          }),
        );
      }
      const sistolica = numeroOIndefinido(c.paSistolica);
      const diastolica = numeroOIndefinido(c.paDiastolica);
      if (sistolica !== undefined && diastolica !== undefined) {
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: PA_PANEL_LOINC, display: 'Presión arterial' }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            component: [
              { code: { coding: [{ system: 'http://loinc.org', code: PA_SISTOLICA_LOINC, display: 'Sistólica' }] }, valueQuantity: { value: sistolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
              { code: { coding: [{ system: 'http://loinc.org', code: PA_DIASTOLICA_LOINC, display: 'Diastólica' }] }, valueQuantity: { value: diastolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
            ],
          }),
        );
      }

      // D (ingreso) y O suelen repetir el diagnóstico principal: un solo Condition por texto+CIE.
      const yaCreados = new Set<string>();
      for (const d of [...estado.diagnosticosIngreso, ...estado.diagnosticos]) {
        const texto = d.diagnostico.texto.trim();
        const clave = `${texto.toLowerCase()}|${d.diagnostico.cie10?.code ?? ''}`;
        if (texto === '' || yaCreados.has(clave)) continue;
        yaCreados.add(clave);
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d.diagnostico), recordedDate: ahora });
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  function bloque(clave: ClaveValores, tituloClave: string, prefijo: string, lista: readonly CampoSimple[], columnas?: 2 | 3 | 4): JSX.Element {
    return (
      <Bloque titulo={t(`formularios.f058.${tituloClave}`)}>
        <CamposSimples prefijoId={`f058-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
      </Bloque>
    );
  }

  function listaDiagnosticos(clave: 'diagnosticosIngreso' | 'diagnosticos', tituloClave: string): JSX.Element {
    const lista: readonly DiagnosticoConMarca[] = estado[clave];
    return (
      <Bloque titulo={t(`formularios.f058.${tituloClave}`)}>
        <p className="text-xs text-hce-muted">{t('formularios.f058.diagnosticoNota')}</p>
        {lista.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t(`formularios.f058.${tituloClave}`)} ${i + 1}`}
            marcaLabel={t('formularios.f058.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, [clave]: lista.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      {bloque('personales', 'personalesTitulo', 'per', CAMPOS_PERSONALES_058, 3)}
      {bloque('cuidador', 'cuidadorTitulo', 'cui', CAMPOS_CUIDADOR_058, 3)}
      {listaDiagnosticos('diagnosticosIngreso', 'diagnosticoIngresoTitulo')}
      <Bloque titulo={t('formularios.f058.patologiaIngresoTitulo')}>
        <CamposSimples prefijoId="f058-pat" campos={CAMPOS_PATOLOGIA_INGRESO_058} valores={estado.patologiaIngreso} onChange={(v) => setEstado({ ...estado, patologiaIngreso: v })} />
      </Bloque>
      <Bloque titulo={t('formularios.f058.motivoEnfermedadTitulo')}>
        <CamposSimples prefijoId="f058-mot" campos={CAMPOS_MOTIVO_ENFERMEDAD_058} valores={estado.motivoEnfermedad} onChange={(v) => setEstado({ ...estado, motivoEnfermedad: v })} />
      </Bloque>
      {bloque('antecedentes', 'antecedentesTitulo', 'ant', CAMPOS_ANTECEDENTES_058, 4)}
      {bloque('habitos', 'habitosTitulo', 'hab', CAMPOS_HABITOS_058, 4)}
      {bloque('conocimiento', 'conocimientoTitulo', 'con', CAMPOS_CONOCIMIENTO_058, 3)}
      {bloque('constantes', 'constantesTitulo', 'cte', CAMPOS_CONSTANTES_058, 4)}
      {imc !== undefined && <p className="text-sm font-medium text-hce-ink">{t('formularios.f058.imcValor', { valor: imc.toFixed(1) })}</p>}
      <Bloque titulo={t('formularios.f058.examenFisicoTitulo')}>
        <CamposSimples prefijoId="f058-exa" campos={CAMPOS_EXAMEN_FISICO_058} valores={estado.examenFisico} onChange={(v) => setEstado({ ...estado, examenFisico: v })} />
      </Bloque>
      {bloque('sintomas', 'sintomasTitulo', 'sin', CAMPOS_SINTOMAS_058, 4)}
      {bloque('componentes', 'componentesTitulo', 'com', CAMPOS_COMPONENTES_058, 4)}
      <Bloque titulo={t('formularios.f058.familiogramaTitulo')}>
        <CamposSimples prefijoId="f058-fam" campos={CAMPOS_FAMILIOGRAMA_058} valores={estado.familiograma} onChange={(v) => setEstado({ ...estado, familiograma: v })} />
      </Bloque>
      {listaDiagnosticos('diagnosticos', 'diagnosticoTitulo')}
      {bloque('plan', 'planTitulo', 'plan', CAMPOS_PLAN_058, 2)}
      {bloque('informante', 'informanteTitulo', 'inf', CAMPOS_INFORMANTE_058, 2)}
      <Bloque titulo={t('formularios.f058.responsableTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
